

































































































































































































import { Vue, Prop, Component, Watch } from 'vue-property-decorator';
import { Entry, Reason as EntryReason, formatDecimal, parseFormattedNumber } from './types/Entry';
import { cloneDeep } from 'lodash';
import i18nService from "@/services/i18n"


interface Reason {
    uuid: string
    sum: number
    reasonRu: string
    reasonKk: string
    editing: boolean
}

interface NewReason {
    sum: number | null
    reasonRu: string
    reasonKk: string
}

const trPath = 'modules.budget_execution_monitoring.reports.app19'

@Component
export default class ReasonInput extends Vue {

    // #region Props
    @Prop({
        required: true,
        default: []
    })
    private paramEntries!: Entry[]

    @Prop({
        required: true
    })
    private userId!: string

    @Prop({
        required: true
    })
    public isAgreedForm!: boolean
    // #endregion

    // #region Data
    private entries: Entry[] = cloneDeep(this.paramEntries)

    public currentEntry: Entry = cloneDeep(this.paramEntries[0])

    public kbkOptions: string[] = []
    public currentKbk: string = ''

    public projectCodeOptions: string[] = []
    public currentProjectCode: string = ''

    public reasons: Reason[] = []

    private dirty = false

    public newReason: NewReason = { sum: null, reasonRu: '', reasonKk: '' }
    // #endregion

    // #region Getters/Watchers
    get totalSum(): number {
        return this.reasons.reduce((acc, r) => acc + (r.sum || 0), 0)
    }

    get remainderSum(): number {
        return Number(
            (Math.abs(this.currentEntry.undevelopedSum) - this.totalSum).toFixed(2)
        )
    }

    get undevelopedProgress(): number {
        return Math.min(100, (this.totalSum / (Math.abs(this.currentEntry.undevelopedSum || 1))) * 100)
    }

    get totalProgress(): number {
        return (this.totalSum / (Math.abs(this.currentEntry.undevelopedSum || 1))) * 100
    }

    get cappedTotalProgress(): number {
        return Math.min(100, this.totalProgress)
    }

    get isLastCurrentEntry(): boolean {
        return this.entries[this.entries.length - 1].id === this.currentEntry.id
    }

    @Watch('currentEntry')
    private async onCurrentEntryChange(newEntry: Entry, oldEntry: Entry) {
        this.reasons = newEntry.reasons.map(it => {
            const mapped: Reason = {
                uuid: this.generateReasonUuid(),
                sum: it.sum,
                reasonRu: it.reasonRu,
                reasonKk: it.reasonKk,
                editing: false,
            }
            return mapped
        })

        this.dirty = false
    }
    // #endregion

    // #region LifeCycle
    private async created() {
        if (this.entries.length === 0) {
            return
        }

        this.kbkOptions = [...new Set(this.entries.map(it => it.formattedRepublicKbk))]
        this.currentKbk = this.kbkOptions[0]
        await this.onKbkInput(this.currentKbk)
    }
    // #endregion

    // #region Methods
    public onNumberInput(value: string | null, reason: any) {
        const parsed = value == null ? null : parseFormattedNumber(value)
        reason.sum = parsed
    }

    public tr(key: string): string {
        const text = String(this.$t(`${trPath}.${key}`))
        return text
    }

    public editReason(index: number): void {
        this.reasons[index].editing = true
        this.dirty = true
    }

    private validate(reason: Reason | NewReason, isEdit: boolean): boolean {
        if (!reason.sum || !reason.reasonRu || !reason.reasonKk) {
            this.makeToast('warning', this.tr('message'), this.tr('mandatory_fields'))
            return false
        }

        const newRemainder = isEdit ? this.remainderSum : this.remainderSum - reason.sum
        if (newRemainder < 0) {
            this.makeToast('warning', this.tr('message'), this.tr('reasons_exceed_undeveloped'))
            return false
        }

        return true
    }

    public confirmReason(index: number): void {
        if (!this.validate(this.reasons[index], true))
            return
        this.reasons[index].editing = false
        this.dirty = true
    }

    public removeReason(index: number): void {
        this.reasons.splice(index, 1)
        this.dirty = true
    }

    public addReason(): void {
        if (!this.validate(this.newReason, false))
            return
        this.reasons.push({
            uuid: this.generateReasonUuid(),
            sum: this.newReason.sum ?? 0,
            reasonRu: this.newReason.reasonRu,
            reasonKk: this.newReason.reasonKk,
            editing: false,
        })
        this.dirty = true
        this.clearNew()
    }

    public onClearReasons() {
        this.$bvModal.msgBoxConfirm(
            this.$t('modules.budget_execution_monitoring.reports.app_9.reason_input.confirm_delete').toString(),
            {
                title: this.$t('modules.budget_execution_monitoring.reports.app_9.confirmation').toString(),
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: this.$t('modules.budget_execution_monitoring.input_forms.massage_tools.yes').toString(),
                cancelTitle: this.$t('modules.budget_execution_monitoring.input_forms.massage_tools.cancellation').toString(),
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }
        )
            .then(value => {
                if (value) {
                    this.reasons = []
                    this.dirty = true
                }
            })
            .catch(error => {
                this.makeToast('danger', this.$t('modules.budget_execution_monitoring.reports.app_9.save_error'), error.toString())
            })
    }

    clearNew(): void {
        this.newReason = { sum: null, reasonRu: '', reasonKk: '' }
    }

    public formatDecimal = formatDecimal

    private generateReasonUuid(): string {
        const uuid: string = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
            const random = Math.random() * 16 | 0
            const v = character == 'x' ? random : (random & 0x3 | 0x8)
            return v.toString(16)
        })

        return uuid
    }

    public async onNext() {
        await this.checkThenSave()
        
        if (this.isLastCurrentEntry) {
            return
        }

        const index = this.entries.findIndex(it => it.id === this.currentEntry.id)
        const nextEntry = this.entries[index + 1]
        await this.onKbkInput(nextEntry.formattedRepublicKbk)
        this.currentKbk = nextEntry.formattedRepublicKbk
        await this.onProjectInput(nextEntry.projectCode)
        this.currentProjectCode = nextEntry.projectCode
        this.currentEntry = nextEntry
    }

    public async onBack() {
        await this.checkThenSave()
        this.$emit('onBack')
    }

    private async checkThenSave() {
        if (this.dirty) {
            await this.saveReasons()
            this.dirty = false
        }
    }

    private async saveReasons() {
        type ReasonEntryRequest = { // TODO: Move to outer scope
            sum: number
            name_ru: string
            name_kk: string
        }
        type EditReasonsRequest = { // TODO: Move to outer scope
            parent_id: number
            user_id: string
            entries: ReasonEntryRequest[]
        }

        try {
            const reasonEntries: ReasonEntryRequest[] = this.reasons.map(it => ({
                sum: it.sum,
                name_ru: it.reasonRu,
                name_kk: it.reasonKk,
            }))

            const requestBody: EditReasonsRequest = {
                parent_id: this.currentEntry.id!!,
                user_id: this.userId,
                entries: reasonEntries,
            }

            const editedEntry = this.entries.find(it => it.id === this.currentEntry.id)!!
            editedEntry.reasons = this.reasons.map(it => new EntryReason(
                1, // Dummy, irrelevant id
                it.sum,
                it.reasonRu,
                it.reasonKk,
            ))

            const response = await fetch('/api-py/application/input-forms/19/reasons', {
                method: 'PUT',
                body: JSON.stringify(requestBody),
            })

            if (!response.ok) {
                const errorMessage = await response.json().catch(() => null)
                throw new Error(errorMessage || `Request failed with status ${response.status}`)
            }

            const responseBody = await response.json()
            responseBody?.violations?.forEach((it: any) => {
                this.makeToast('warning', this.tr('message'), it[`name_${i18nService.locale}`])
            })

            this.makeToast('success', this.$t('modules.common.saving'), this.$t('modules.common.data_saved_successfully'))
        } catch (error) {
            console.error('Error during editing data:', error)
            this.makeToast('danger', this.$t('modules.budget_execution_monitoring.reports.app_9.save_error'), error.toString())
        } finally {
            // this.loading = false TODO: Consider implementing loader
        }
    }

    public async onKbkInput(kbkCode: string) {
        await this.checkThenSave()

        const kbkFilteredEntries: Entry[] = this.entries.filter(it => it.formattedRepublicKbk === kbkCode)
        this.projectCodeOptions = [...new Set(kbkFilteredEntries.map(it => it.projectCode))]
        this.currentProjectCode = this.projectCodeOptions[0]
        this.currentEntry = this.entries.find(entry =>
            entry.formattedRepublicKbk === kbkCode
            && entry.projectCode === this.currentProjectCode
        )!
    }
    
    public async onProjectInput(projectCode: string) {
        await this.checkThenSave()

        this.currentEntry = this.entries.find(entry =>
            entry.formattedRepublicKbk === this.currentKbk
            && entry.projectCode === projectCode
        )!
    }

    private makeToast(variant: any, title: any, toastBody: any) {
        this.$bvToast.toast(toastBody, {
            title: title,
            variant: variant,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 5000,
            appendToast: true
        })
    }
    // #endregion
}
